"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";

import { cn } from "@/lib/utils";

/**
 * The Twin mark with a working state, in one element. Each pigment layer is
 * one stroked circle on the centre line of its band. At rest the stroke is
 * the full band width, so the six strokes tile into the solid mark; active,
 * each stroke thins just enough to show a hairline between bands while the
 * breathe and drift amplitudes ramp up from nothing; the core stays a disc.
 * Both changes are transitions on the same elements, so the mark never
 * jumps between states either way.
 *
 * Three ways to drive it, which combine:
 * - `active`: controlled. Keep it true while work is in flight, as with
 *   `isPending` from a mutation or query.
 * - `durationSeconds`: timed. Works for that long after mount, or again
 *   whenever `restartKey` changes, then settles.
 * - `minActiveSeconds`: a floor so a fast request still shows a full breath
 *   rather than a flicker.
 * - `delaySeconds`: defer the start, so work that finishes quickly never
 *   lights the mark at all, or a timed run begins after a beat.
 * - `hoverSeconds`: every mark also answers a hover with a short run, three
 *   seconds by default; a hover while it is already working does nothing.
 *   Pass 0 to opt out.
 * `onSettle` fires once the mark has fully returned to rest, after the
 * closing transition, so a caller can chain what comes next.
 *
 * Geometry matches `TwinMark` exactly (32-unit frame, radii 13.2 → 3.2, the
 * same `bleed` scale), so it can replace it anywhere the state can change.
 */
const RINGS = [
  { r: 13.2, fill: "#D91B5C" },
  { r: 11.2, fill: "#E8501C" },
  { r: 9.2, fill: "#D49A00" },
  { r: 7.2, fill: "#00A56A" },
  { r: 5.2, fill: "#008FB8" },
  { r: 3.2, fill: "#7456E8" },
] as const;
const BLEED = 16 / 13.2;
const BAND = 2;
/** Bands overlap outward by this much so no anti-aliased seam shows where
 *  one solid band meets the next; inner bands paint over outer ones. */
const BAND_OVERLAP = 0.3;
/** Active bands thin only slightly: a hairline of ground between rings,
 *  each band still a solid colour, so the working mark keeps its weight. */
const RING_STROKE = 1.8;
/** Matches the transition length in twin-spinner.css. */
export const TWIN_LIVE_MARK_SETTLE_MS = 480;
const noise = (seed: number) => {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
};
const RHYTHM = RINGS.map((_, layer) => ({
  "--mlx-twin-breathe": `${(1.8 + noise(layer + 1) * 1.4).toFixed(2)}s`,
  "--mlx-twin-breathe-delay": `${(-noise(layer + 7) * 3).toFixed(2)}s`,
  "--mlx-twin-drift": `${(2.2 + noise(layer + 13) * 1.6).toFixed(2)}s`,
  "--mlx-twin-drift-delay": `${(-noise(layer + 19) * 4).toFixed(2)}s`,
  "--mlx-twin-wobble": `${(0.6 + noise(layer + 23) * 0.6).toFixed(2)}px`,
})) as CSSProperties[];

export type TwinLiveMarkProps = {
  /** The Twin is working: the solid mark opens into breathing, drifting rings. */
  active?: boolean;
  /** Work for this long after mount (or after `restartKey` changes), then settle. */
  durationSeconds?: number;
  /** Change to run a timed animation again. */
  restartKey?: string | number;
  /** Stay lit at least this long once lit, so quick work still reads. */
  minActiveSeconds?: number;
  /** Wait this long after work starts before lighting the mark. */
  delaySeconds?: number;
  /** Run for this long when hovered, unless already working. 0 disables. */
  hoverSeconds?: number;
  /** Called once the mark is fully at rest again. */
  onSettle?: () => void;
  /** Speed multiplier for the breathe and drift: 0.7 calm, 1 normal, 1.4 brisk. */
  tempo?: number;
  /** Fill the frame edge-to-edge, as `TwinMark` does inside avatars. */
  bleed?: boolean;
  className?: string;
  /** Announced while working. Omit for a purely decorative mark. */
  label?: string;
};

export function TwinLiveMark({
  active = false,
  durationSeconds,
  restartKey,
  minActiveSeconds = 0,
  delaySeconds = 0,
  hoverSeconds = 3,
  onSettle,
  tempo = 1,
  bleed = false,
  className,
  label,
}: TwinLiveMarkProps) {
  const timed = durationSeconds !== undefined && durationSeconds > 0;
  const [timedActive, setTimedActive] = useState(timed);
  useEffect(() => {
    if (!timed) {
      setTimedActive(false);
      return;
    }
    setTimedActive(true);
    const timer = window.setTimeout(
      () => setTimedActive(false),
      durationSeconds * 1000,
    );
    return () => window.clearTimeout(timer);
  }, [timed, durationSeconds, restartKey]);

  const [hoverKey, setHoverKey] = useState(0);
  const [hoverActive, setHoverActive] = useState(false);
  // Read the length at hover time only, so changing hoverSeconds never
  // replays an earlier hover.
  const hoverLength = useRef(hoverSeconds);
  hoverLength.current = hoverSeconds;
  useEffect(() => {
    if (hoverKey === 0 || hoverLength.current <= 0) return;
    setHoverActive(true);
    const timer = window.setTimeout(
      () => setHoverActive(false),
      hoverLength.current * 1000,
    );
    return () => window.clearTimeout(timer);
  }, [hoverKey]);

  const wanted = active || timedActive || hoverActive;
  const [lit, setLit] = useState(wanted && delaySeconds <= 0);
  const litSince = useRef(0);
  const everLit = useRef(wanted && delaySeconds <= 0);
  useEffect(() => {
    if (wanted) {
      if (lit) return;
      const light = () => {
        litSince.current = Date.now();
        everLit.current = true;
        setLit(true);
      };
      if (delaySeconds <= 0) {
        light();
        return;
      }
      const timer = window.setTimeout(light, delaySeconds * 1000);
      return () => window.clearTimeout(timer);
    }
    if (!lit) return;
    const hold = Math.max(
      0,
      minActiveSeconds * 1000 - (Date.now() - litSince.current),
    );
    const timer = window.setTimeout(() => setLit(false), hold);
    return () => window.clearTimeout(timer);
  }, [wanted, lit, minActiveSeconds, delaySeconds]);

  const settle = useRef(onSettle);
  settle.current = onSettle;
  useEffect(() => {
    if (lit || !everLit.current) return;
    const timer = window.setTimeout(
      () => settle.current?.(),
      TWIN_LIVE_MARK_SETTLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [lit]);

  const scale = bleed ? BLEED : 1;
  return (
    <svg
      aria-hidden={label ? undefined : true}
      aria-label={label}
      className={cn("mlx-twin-live shrink-0", className)}
      data-active={lit}
      data-slot="twin-live-mark"
      data-state={lit ? "working" : "resting"}
      focusable="false"
      onPointerEnter={
        hoverSeconds > 0
          ? () => {
              if (!lit) setHoverKey((key) => key + 1);
            }
          : undefined
      }
      role={label ? "status" : undefined}
      style={
        {
          "--mlx-twin-band": `${((BAND + BAND_OVERLAP) * scale).toFixed(3)}px`,
          "--mlx-twin-ring": `${(RING_STROKE * scale).toFixed(3)}px`,
          "--mlx-twin-tempo": tempo,
        } as CSSProperties
      }
      viewBox="0 0 32 32"
    >
      {RINGS.map((ring, layer) => {
        const inner = layer < RINGS.length - 1 ? RINGS[layer + 1]!.r : 0;
        // The core is a disc in both states: a stroke as wide as its radius
        // closes the hole, and it never thins.
        const core = inner === 0;
        return (
          <g key={ring.fill} className="mlx-twin-live__breathe" style={RHYTHM[layer]}>
            <g className="mlx-twin-live__drift">
              <circle
                className="mlx-twin-live__band"
                cx="16"
                cy="16"
                data-core={core || undefined}
                fill="none"
                r={((ring.r + inner) / 2) * scale}
                stroke={ring.fill}
                style={
                  core
                    ? ({
                        "--mlx-twin-band": `${(ring.r * scale + BAND_OVERLAP).toFixed(3)}px`,
                        "--mlx-twin-ring": `${(ring.r * scale + BAND_OVERLAP).toFixed(3)}px`,
                      } as CSSProperties)
                    : undefined
                }
              />
            </g>
          </g>
        );
      })}
    </svg>
  );
}
